import { z } from "zod";
/** Complete semantic registry; component styles contain no palette fallbacks. */
export const UI_COLOR_DEFAULTS = {
    "--cx-bg": "#080b13",
    "--cx-surface": "#0d1220",
    "--cx-surface-2": "#101728",
    "--cx-surface-3": "#131a2b",
    "--cx-surface-4": "#1c2438",
    "--cx-canvas": "#05070c",
    "--cx-border": "#1b2338",
    "--cx-border-strong": "#223050",
    "--cx-text": "#e8ecf5",
    "--cx-text-2": "#cfd6e4",
    "--cx-text-muted": "#9aa4b8",
    "--cx-text-dim": "#7d879c",
    "--cx-text-faint": "#6b7591",
    "--cx-primary": "#e5173f",
    "--cx-win": "#6ce9a6",
    "--cx-warn": "#f79009",
    "--cx-bet-start": "#1fd07c",
    "--cx-bet-end": "#128f5c",
    "--cx-bet-shadow": "#0b6b3c",
    "--cx-cashout-start": "#ffa93a",
    "--cx-cashout-end": "#f07d09",
    "--cx-cashout-shadow": "#a85405",
    "--cx-bet-won-bg": "#0a2318",
    "--cx-bet-won-border": "#17b26a",
    "--cx-history-bg": "#0a0e19",
    "--cx-history-low-text": "#6ce9a6",
    "--cx-history-low-bg": "#06210f",
    "--cx-history-low-border": "#0d4023",
    "--cx-history-mid-text": "#53b1fd",
    "--cx-history-mid-bg": "#0b1b2e",
    "--cx-history-mid-border": "#143352",
    "--cx-history-high-text": "#b692f6",
    "--cx-history-high-bg": "#1a1030",
    "--cx-history-high-border": "#2d1a4d",
    "--cx-history-extreme-text": "#f04438",
    "--cx-history-extreme-bg": "#2a0d14",
    "--cx-history-extreme-border": "#4a1420",
    "--cx-color-bg": "#080b13",
    "--cx-color-surface": "#0d1220",
    "--cx-color-primary": "#e5173f",
    "--cx-color-accent": "#53b1fd",
    "--cx-color-success": "#6ce9a6",
    "--cx-color-warning": "#f79009",
    "--cx-color-danger": "#f04438",
    "--cx-color-text": "#e8ecf5",
    "--cx-color-muted": "#9aa4b8",
    "--cx-color-history-low": "#6ce9a6",
    "--cx-color-history-mid": "#53b1fd",
    "--cx-color-history-high": "#f04438",
    "--cx-color-vehicle-primary": "#e5173f",
    "--cx-color-vehicle-secondary": "#e8ecf5",
    "--cx-color-vehicle-accent": "#1a1a1a",
    "--cx-multiplier-glow": "rgb(229 23 63 / 45%)",
    "--cx-on-bet": "#e8ecf5",
    "--cx-on-cashout": "#e8ecf5",
    "--cx-shadow-color": "#000000",
};
export const Paint = z.string().refine((value) => {
    if (/^#[0-9a-f]{6}$/i.test(value))
        return true;
    const match = /^rgb\(\s*(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})(?:\s*\/\s*(\d+(?:\.\d+)?)(%)?)?\s*\)$/.exec(value);
    return Boolean(match &&
        match.slice(1, 4).every((channel) => Number(channel) <= 255) &&
        (match[4] === undefined || Number(match[4]) <= (match[5] ? 100 : 1)));
}, "expected #RRGGBB or rgb(R G B / alpha), channels 0..255 and alpha 0..1 or 0..100%");
export const UiColors = z
    .strictObject(Object.fromEntries(Object.keys(UI_COLOR_DEFAULTS).map((key) => [key, Paint])))
    .refine((value) => /^#[0-9a-f]{6}$/i.test(value["--cx-shadow-color"]), {
    path: ["--cx-shadow-color"],
    message: "shadow role requires opaque #RRGGBB",
});
export const VEHICLE_PAINT_DEFAULTS = {
    highlight: "#ffffff",
    shadow: "#000000",
    outline: "#e8ecf5",
};
/** Neutral alpha masks and tint sources, independent of the visual palette. */
export const COMPOSITE_PAINT = {
    transparent: "rgba(0,0,0,0)",
    opaque: "rgba(0,0,0,1)",
    tint: "#ffffff",
};
export const SceneColors = z.strictObject({
    skyBaseTop: Paint,
    skyBaseBottom: Paint,
    skyMiddleTop: Paint,
    skyMiddleBottom: Paint,
    skyUpperTop: Paint,
    skyUpperBottom: Paint,
    planeSkyStart: Paint,
    planeSkyEnd: Paint,
    rays: Paint,
    clouds: Paint,
    horizon: Paint,
    frame: Paint,
    ground: Paint,
    disc: Paint,
    discEdge: Paint,
    stars: Paint,
    light: Paint,
    trail: Paint,
    curve: Paint,
    curveFill: Paint,
    curveFillEnd: Paint,
    axis: Paint,
    axisMinor: Paint,
    axisText: Paint,
    flame: Paint,
    flameEnd: Paint,
    smoke: Paint,
    debris: Paint,
});
const OpaquePaint = z.string().regex(/^#[0-9a-f]{6}$/i, "this role requires opaque #RRGGBB");
export const SceneColorRole = SceneColors.keyof();
export const Colors = z.strictObject({
    ui: UiColors,
    vehicle: z.strictObject({
        primary: OpaquePaint,
        secondary: OpaquePaint,
        accent: OpaquePaint,
        highlight: OpaquePaint,
        shadow: OpaquePaint,
        outline: OpaquePaint,
    }),
    scene: SceneColors.refine((value) => Object.values(value).every((color) => /^#[0-9a-f]{6}$/i.test(color)), "scene roles require opaque #RRGGBB; opacity belongs to scenery parameters"),
});
/** Relative luminance of opaque sRGB paints. */
export function luminance(color) {
    const match = /^#([0-9a-f]{6})$/i.exec(color);
    if (!match)
        throw new Error("Luminance validation requires opaque six-digit sky colors");
    const value = parseInt(match[1], 16);
    return [value >>> 16, (value >>> 8) & 255, value & 255]
        .map((channel) => {
        const v = channel / 255;
        return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    })
        .reduce((sum, v, i) => sum + v * [0.2126, 0.7152, 0.0722][i], 0);
}
export function contrastRatio(a, b) {
    const first = luminance(a), second = luminance(b);
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}
//# sourceMappingURL=color-registry.js.map